import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import DistList from "../components/DistList";
import { fmtNum, statusClass } from "../lib/format";

export default function WebGateway() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    apiGet("/webgw/summary").then(setD);
  }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;

  return (
    <>
      <Masthead
        module="webgw"
        eyebrow="Network Security · Web Gateway"
        title="Web Gateway Health"
        subtitle="FortiGate web access · allowed vs blocked · RMM tools · IPS threats · last 24h"
        meta={<div>Source: <strong>FortiGate NG-SIEM</strong><br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="flex items-center justify-between mb-3">
          <div className="cv-section-label !mb-0">User View · Access &amp; Category Enforcement</div>
          <span className="text-[11px] small-caps font-bold px-3 py-1 rounded-full tracking-[0.14em]"
                style={{ background: "rgba(74,90,106,0.14)", color: "var(--cv-slate)", border: "1px solid rgba(74,90,106,0.28)" }}>
            SWG · User-Centric
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <Kpi testid="wg-users"    label="Unique Users"        value={fmtNum(k.unique_users)}     sub="distinct authenticated users · 24h" onClick={() => nav("/drill/identities?kind=human")} />
          <Kpi testid="wg-srcips"   label="Unique Source IPs"   value={fmtNum(k.unique_source_ips)} sub="internal + roaming endpoints" onClick={() => nav("/drill/identities?kind=human")} />
          <Kpi testid="wg-cats"     label="Blocked Categories"  value={k.blocked_categories}       sub="URL-filter policy classes" onClick={() => nav("/drill/firewalls")} />
          <Kpi testid="wg-events"   label="Total Events"        value={k.total_events}             sub="web sessions inspected · 24h" onClick={() => nav("/drill/firewalls")} />
          <Kpi testid="wg-blocked"  label="Blocked Events"      value={k.blocked}                  sub="deny / drop / block" alert onClick={() => nav("/drill/firewalls")} />
          <Kpi testid="wg-allowed"  label="Allowed Events"      value={k.allowed}                  sub="accept / allow" onClick={() => nav("/drill/firewalls")} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Panel testid="panel-rmm" title="Remote Access Tools (RMM)" subtitle="AnyDesk · TeamViewer · RDP · VNC · ScreenConnect · SSH">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Tool</th>
                  <th className="text-left px-2 py-2 small-caps">User</th>
                  <th className="text-left px-2 py-2 small-caps">Source IP</th>
                  <th className="text-right px-2 py-2 small-caps">Sessions</th>
                  <th className="text-left px-2 py-2 small-caps">Status</th>
                </tr>
              </thead>
              <tbody>
                {d.rmm_sessions.map((r) => (
                  <tr key={r.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{r.tool}</strong></td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{r.user}</td>
                    <td className="px-2 py-2 border-b font-mono text-[14px]" style={{ borderColor: "var(--cv-line)" }}>{r.src_ip}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{r.sessions}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${statusClass(r.status)}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel testid="panel-user-activity" title="User Activity" subtitle="Per-user traffic — who is talking to what, how much">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">User</th>
                  <th className="text-right px-2 py-2 small-caps">Events</th>
                  <th className="text-right px-2 py-2 small-caps">Allowed</th>
                  <th className="text-right px-2 py-2 small-caps">Blocked</th>
                  <th className="text-left px-2 py-2 small-caps">Risk</th>
                </tr>
              </thead>
              <tbody>
                {d.user_activity.map((u) => (
                  <tr key={u.id} className="cv-row-clickable" onClick={() => nav(`/drill/identities?q=${encodeURIComponent(u.user)}`)}>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{u.user}</strong></td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(u.events)}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(u.allowed)}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(u.blocked)}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${statusClass(u.risk)}`}>{u.risk}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>

        <div className="cv-section-label">Top Blocked Traffic</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <Panel testid="panel-blocked-ips" title="Top Blocked Source IPs" subtitle="External origins of denied traffic">
            {d.blocked_ips.map((b) => (
              <div key={b.id} className="flex justify-between py-1.5 border-b text-[14px]" style={{ borderColor: "var(--cv-line)" }}>
                <span><strong className="font-mono">{b.src}</strong> <span className="opacity-60">({b.country})</span></span>
                <span className="tabular-nums font-bold" style={{ color: "var(--cv-crimson)" }}>{fmtNum(b.blocks)}</span>
              </div>
            ))}
          </Panel>
          <Panel testid="panel-blocked-apps" title="Top Blocked Applications" subtitle="Apps denied by policy">
            {d.blocked_apps.map((a) => (
              <div key={a.id} className="flex justify-between py-1.5 border-b text-[14px]" style={{ borderColor: "var(--cv-line)" }}>
                <span><strong>{a.app}</strong></span>
                <span className="tabular-nums font-bold" style={{ color: "var(--cv-crimson)" }}>{fmtNum(a.blocks)}</span>
              </div>
            ))}
          </Panel>
          <Panel testid="panel-blocked-countries" title="Blocked by Source Country" subtitle="Geographic origin of blocks">
            {d.blocked_countries.map((c) => (
              <div key={c.country} className="flex justify-between py-1.5 border-b text-[14px]" style={{ borderColor: "var(--cv-line)" }}>
                <span><strong>{c.country}</strong></span>
                <span className="tabular-nums font-bold" style={{ color: "var(--cv-crimson)" }}>{fmtNum(c.value)}</span>
              </div>
            ))}
          </Panel>
          <Panel testid="panel-block-types" title="Blocked by Action Type" subtitle="Deny vs dropped vs reset">
            <DistList items={d.blocked_types} />
          </Panel>
        </div>

        <div className="cv-section-label">Top Allowed Traffic</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel testid="panel-allowed-apps" title="Top Allowed Applications" subtitle="Shadow IT flagged by app risk">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Application</th>
                  <th className="text-right px-2 py-2 small-caps">Connections</th>
                  <th className="text-left px-2 py-2 small-caps">App Risk</th>
                </tr>
              </thead>
              <tbody>
                {d.allowed_apps.map((a) => (
                  <tr key={a.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{a.app}</strong></td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(a.connections)}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                      <span className={`status-pill ${statusClass(a.risk)}`}>{a.risk}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel testid="panel-allowed-dest" title="Top Allowed External Destinations" subtitle="Bytes per cloud destination">
            <table className="w-full text-[14px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  <th className="text-left px-2 py-2 small-caps">Destination</th>
                  <th className="text-left px-2 py-2 small-caps">Country</th>
                  <th className="text-right px-2 py-2 small-caps">Sessions</th>
                  <th className="text-right px-2 py-2 small-caps">Bytes</th>
                </tr>
              </thead>
              <tbody>
                {d.allowed_dest.map((a) => (
                  <tr key={a.id} className="cv-row-clickable">
                    <td className="px-2 py-2 border-b font-mono text-[14px]" style={{ borderColor: "var(--cv-line)" }}>{a.dest}</td>
                    <td className="px-2 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{a.country}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{fmtNum(a.sessions)}</td>
                    <td className="px-2 py-2 border-b text-right tabular-nums" style={{ borderColor: "var(--cv-line)" }}>{a.bytes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>
      </div>
    </>
  );
}
